// @need-translate
import {InfiniteData} from '@tanstack/react-query';
import {parse} from 'qs';
import {MouseEventHandler, useCallback, useEffect, useMemo} from 'react';
import {getIsAgeRestricted} from '../../../../common/getIsAgeRestricted';
import {t} from '../../../../common/getTranslations';
import {omitNullUndefined} from '../../../../common/omitNullUndefined';
import {requestContext} from '../../../../common/requestContext';
import {formatWithCommas} from '../../../../common/shared/utils/formatWithCommas';
import {pushToRouter} from '../../../../common/shared/utils/pushToRouter';
import {twMerge} from '../../../../common/twMerge';
import {Grid} from '../../../../design-system/atoms/Grid/Grid';
import {IconType} from '../../../../design-system/atoms/Icon/IconType.enum';
import {Loading} from '../../../../design-system/atoms/Loading/Loading';
import {TabPositionControl} from '../../../../design-system/atoms/TabPositionControl/TabPositionControl';
import {CheckBoxSize} from '../../../../design-system/molecules/CheckBox/CheckBoxSize.enum';
import {BelowDefaultTab} from '../../../../design-system/organisms/BelowDefaultTab/BelowDefaultTab';
import {MainTab} from '../../../../design-system/organisms/MainTab/MainTab';
import {NormalListViewItem} from '../../../../design-system/organisms/NormalListViewItem/NormalListViewItem';
import {useDialog} from '../../../../dialog/useDialog.hook';
import {
  SearchKeywordInput,
  SearchKeywordQuery,
} from '../../../../graphql/generated/types';
import {aggregateAndSendLog} from '../../../../logging/aggregateAndSendLog';
import {LOG_CLICK_EVENT} from '../../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {TrackEventObject} from '../../../../logging/TrackEventObject.interface';
import {useLogin} from '../../../../user/useLogin.hook';
import {useInfiniteSearchKeywordQuery} from '../../../../wrapper/searchWrapper';
import {
  selectCategoryTabItemList,
  selectCurrentSearchKeywordCategoryUid,
  selectSearchKeyword,
} from '../../state/selector';

interface SearchKeywordContainerProps {
  data: InfiniteData<SearchKeywordQuery>;
  searchKeywordInput: SearchKeywordInput;
}

export function SearchKeywordContainer({
  data: infiniteData,
  searchKeywordInput,
}: SearchKeywordContainerProps) {
  const {isFetching, isFetchingNextPage, isPlaceholderData} =
    useInfiniteSearchKeywordQuery(
      {
        input: {...searchKeywordInput},
      },
      {
        initialPageParam: {input: {...searchKeywordInput}},
        getNextPageParam: () => undefined,
      }
    );

  const data = useMemo(() => selectSearchKeyword(infiniteData), [infiniteData]);
  const categoryTabItems = selectCategoryTabItemList(data);
  const currentCategoryUid = selectCurrentSearchKeywordCategoryUid(data);
  const showOnlyComplete = data?.showOnlyComplete;
  const {user} = useLogin();

  const handleChangeShowOnlyComplete = useCallback(() => {
    pushToRouter({
      ...parse(location.search, {ignoreQueryPrefix: true}),
      showOnlyComplete: !showOnlyComplete,
    });
  }, [showOnlyComplete]);

  const {DialogWrapper, Dialog, showDialog, hideDialog} = useDialog({
    isMobile: requestContext.requestInfo.display.isMobile,
  });

  const handleSelectSortOption = useCallback(
    (sortType: string) => {
      hideDialog();
      pushToRouter({
        ...parse(location.search, {ignoreQueryPrefix: true}),
        sortType,
      });
    },
    [hideDialog]
  );

  const {isMobile, isWebView} = requestContext.requestInfo.display;

  useEffect(() => {
    if (data) {
      aggregateAndSendLog({
        actionName: '검색_완료',
        actionType: 'Event',
        actionKind: 'Search',
        search: {
          search_term: data?.keyword,
          search_results_num: String(data?.total),
        },
        customProps: {
          user_action: 'pv',
          page_category_filter: data.selectedCategoryOption?.name,
          page_sort: data.selectedSortOption?.name,
          completed: data.showOnlyComplete ? 'y' : 'n',
        },
      });
    }
  }, [data]);

  const onClickNormalListItem = useCallback<MouseEventHandler<HTMLDivElement>>(
    event => {
      LOG_CLICK_EVENT('작품_클릭', event);
    },
    []
  );

  const searchCommonCustomProps = useMemo(
    () =>
      ({
        page_category_filter: data?.selectedCategoryOption?.name,
        page_sort: data?.selectedSortOption?.name,
        completed: data?.showOnlyComplete ? 'y' : 'n',
        search_result_term: data?.keyword,
        content_cnt: String(data?.total),
      }) as const,
    [
      data?.keyword,
      data?.selectedCategoryOption?.name,
      data?.selectedSortOption?.name,
      data?.showOnlyComplete,
      data?.total,
    ]
  );

  const {isIos} = requestContext.requestInfo.display;

  if (!data) {
    return null;
  }

  return (
    <>
      <div
        className={twMerge(
          isMobile && 'mb-15pxr',
          !isMobile && 'mb-84pxr',
          (data.total === 0 || isFetching) && 'flex flex-1 flex-col'
        )}
        {...putTiaraObjectProps({
          customProps: {
            ...searchCommonCustomProps,
          },
        })}
      >
        {categoryTabItems && (
          <TabPositionControl
            depth={1}
            isMobile={isMobile}
            isWebView={isWebView}
            disabled={!isMobile}
          >
            <MainTab
              isMobile={isMobile}
              title={`'${data.keyword}'로 검색된 결과`}
              tabItems={categoryTabItems}
              currentTabItemId={currentCategoryUid}
              showBorder={true}
              className={twMerge(
                !isMobile ? 'px-15pxr' : 'p-0',
                isMobile &&
                  'before:absolute before:bottom-0 before:left-0 before:z-[-1] before:h-45pxr before:w-full before:bg-theme-bg-base'
              )}
              tabItemTiaraObject={{
                click: {
                  layer1: '카테고리필터',
                },
                customProps: {
                  user_action: 'click',
                  ...(searchCommonCustomProps && {
                    ...searchCommonCustomProps,
                  }),
                },
              }}
              actionName="카테고리_클릭"
            />
          </TabPositionControl>
        )}
        <TabPositionControl
          depth={2}
          isMobile={isMobile}
          isWebView={isWebView}
          disabled={!isMobile}
          scrollTargetElement={isIos ? document.body : undefined}
        >
          <BelowDefaultTab className="px-15pxr">
            <BelowDefaultTab.TextLabel>
              {t('search_result_content_count', {
                values: {
                  value: formatWithCommas(data.total),
                },
              })}
            </BelowDefaultTab.TextLabel>
            <BelowDefaultTab.CheckBox
              isChecked={data.showOnlyComplete}
              size={CheckBoxSize.Small}
              onClick={e =>
                LOG_CLICK_EVENT(
                  '완결작보기_클릭',
                  e,
                  handleChangeShowOnlyComplete
                )
              }
              {...putTiaraObjectProps({
                click: {
                  layer1: '리스팅옵션',
                  copy: data.showOnlyComplete ? 'off' : 'on',
                },
              })}
            >
              {t('search_result_filter_completed')}
            </BelowDefaultTab.CheckBox>
            <BelowDefaultTab.SelectBox
              iconType={IconType.TabArrowDown}
              onClick={showDialog}
            >
              {data.selectedSortOption.name}
            </BelowDefaultTab.SelectBox>
          </BelowDefaultTab>
        </TabPositionControl>
        {isFetching && !isFetchingNextPage && (
          <div className="flex-center-full">
            <Loading />
          </div>
        )}
        {!isFetching && data.total === 0 && (
          <div className="font-medium1 text-theme-solid-100 flex-center-full">
            {t('search_result_empty')}
          </div>
        )}
        {!isPlaceholderData && (
          <Grid
            cols={isMobile ? 1 : 2}
            tabletCols={2}
            gapPx={0}
            className="my-5pxr"
          >
            {data.list.map(({eventLog, ...item}, index) => {
              const omitEventLog = omitNullUndefined(
                eventLog
              ) as TrackEventObject;
              return (
                <NormalListViewItem
                  key={item.id}
                  item={item}
                  isAgeRestricted={getIsAgeRestricted({
                    age: user?.age,
                    authenticatedAt: user?.authenticatedAt,
                    ageGrade: item.ageGrade,
                    selfCensorship: item.selfCensorship,
                  })}
                  onClick={onClickNormalListItem}
                  {...putTiaraObjectProps({
                    click: {
                      layer1: '검색결과목록',
                      ordnum: String(index + 1),
                    },
                    ...(omitEventLog &&
                      omitEventLog.eventMeta && {
                        eventMeta: omitEventLog.eventMeta,
                      }),
                  })}
                />
              );
            })}
          </Grid>
        )}
      </div>
      <DialogWrapper
        {...putTiaraObjectProps({
          click: {
            layer1: '리스팅옵션',
          },
          customProps: {
            page_category_filter: data.selectedCategoryOption.name,
            page_sort: data.selectedSortOption.name,
            completed: data.showOnlyComplete ? 'y' : 'n',
            search_result_term: data.keyword,
          },
        })}
      >
        <Dialog.Dimmed />
        <Dialog.List className="space-y-0">
          {data.sortOptionList.map(option => (
            <Dialog.Option
              key={option.id}
              onClick={e =>
                LOG_CLICK_EVENT('정렬_클릭', e, () =>
                  handleSelectSortOption(option.param)
                )
              }
              isChecked={option.param === data.selectedSortOption.param}
              {...putTiaraObjectProps({
                click: {
                  copy: option.name,
                },
              })}
            >
              {option.name}
            </Dialog.Option>
          ))}
        </Dialog.List>
        <Dialog.BottomCombination>
          <Dialog.Button onClick={hideDialog}>
            {t('common_cancel_button')}
          </Dialog.Button>
        </Dialog.BottomCombination>
      </DialogWrapper>
    </>
  );
}
